import "../../globals";
import React from "react";
import translate from "@deosite/helpers/langLibrary";
import { RichText } from "prismic-reactjs";

import Box from "@commerce-ui/core2/Box";
import Image from "@deosite/components-new/Image";
import Container from "@commerce-ui/core2/Container";

import PageContainer from "../../containers/UI/PageContainer";

import ProductBox from "../../components/ProductBox";
import CardsSlider from "../../components/CardsSlider";
import ArticleHeader from "../../components/ArticleHeader";
import InfluencerBio from "../../components/InfluencerBio";
import RelatedStory from "../../components/RelatedStory";

import SectionWithConfigurableHeader from "../../sections/SectionWithConfigurableHeader";
import Grid2Cards from "../../sections/Grid2Cards";

import {
  usePrismic,
  fetcher as prismicFetcher
} from "@deosite/api/hooks/prismic/usePrismic";

import Grid from "@commerce-ui/core2/Grid";
import { useRouter } from "next/router";
import MetaTags from "../../components/MetaTags";

const IMAGE_FULL_WIDTH = "full width";
const IMAGE_WITH_MARGIN = "width with margin";
const IMAGE_WITH_LARGER_MARGIN = "width with larger margin";

const ArticlePage = props => {
  const { initialPrismicResponse, initialArticleUid, lang } = props;
  const router = useRouter();

  const { data: prismicResponse } = usePrismic(
    "article",
    initialArticleUid,
    lang,
    initialPrismicResponse
  );

  const article = prismicResponse?.content?.article;
  const influencerData = prismicResponse?.blogger && prismicResponse.blogger[0];

  const relatedArticles = prismicResponse?.related;

  const translations = {
    shopStory: translate("Shop this story"),
    seoDescription: translate("Default seo description"),
    seoTitle: translate("Default seo title"),
    relatedStories: translate("Related stories"),
    inspiration: translate("Inspirations")
  };

  const backLabel = translations.inspiration;
  const backClickFunction = () => {
    router.push("/inspirations", "/inspirations", { shallow: true });
  };

  const shopArticleSection = (
    <Box>
      {prismicResponse?.products?.length > 0 && (
        <Box
          sx={{
            my: "s12",
            overflow: "hidden"
          }}
        >
          <Box
            as={"h5"}
            sx={{
              font: "label2",
              mb: "s9",
              textAlign: "center",
              color: "main"
            }}
          >
            {translations.shopStory}
          </Box>
          <CardsSlider>
            {prismicResponse.products.map((product, index) => (
              <ProductBox key={index} product={product} />
            ))}
          </CardsSlider>
        </Box>
      )}
    </Box>
  );

  const simulateImageModes = (mode, image) => {
    switch (mode) {
      case IMAGE_FULL_WIDTH:
        return <Image image={image} />;
      case IMAGE_WITH_MARGIN:
        return (
          <Container>
            <Grid cols={[24, null, 20, null]}>
              <Box
                sx={{
                  gridColumn: ["3 / span 20", null, "6 / span 10", null]
                }}
              >
                <Image
                  image={image}
                  sizes={
                    "(min-width: 960px) 768px, (max-width: 960px) and (min-width:730px) 420px, (max-width: 730px) and (min-width: 560px) 768px,  (max-width: 560px) and (min-width: 10px) 420px"
                  }
                />
              </Box>
            </Grid>
          </Container>
        );
      case IMAGE_WITH_LARGER_MARGIN:
        return (
          <Container>
            <Grid cols={[1, null, 20, null]}>
              <Box
                sx={{
                  gridColumn: ["span 1", null, "3 / span 16", null]
                }}
              >
                <Image
                  image={image}
                  sizes={
                    "(min-width: 1520px) 1024px, (min-width: 1086px) 768px, (max-width: 1085px) and (min-width: 460px) 420px, (max-width: 460px) and (min-width: 10px) 210px, 1024px"
                  }
                />
              </Box>
            </Grid>
          </Container>
        );
      default:
        return <Image image={image} />;
    }
  };

  return (
    <>
      <MetaTags
        description={
          prismicResponse?.seoDescription
            ? prismicResponse.seoDescription
            : translations.seoDescription
        }
        title={
          prismicResponse?.seoTitle
            ? prismicResponse.seoTitle
            : translations.seoTitle
        }
      />
      <PageContainer
        footerContent={props.footerContent}
        returnLinkLabel={backLabel}
        onReturnLabelClick={backClickFunction}
        dataRequiredToHideLoader={[prismicResponse]}
      >
        {article && (
          <Box
            sx={{
              mt: "s10"
            }}
          >
            <ArticleHeader
              title={article.title}
              subtitle={article.subtitle}
              influencerData={influencerData}
            />

            {article.content &&
              article.content.map((element, index) => {
                if (element.type === "image") {
                  return (
                    <Box key={index} sx={{ mb: "s10" }}>
                      {index === 3 && shopArticleSection}
                      {simulateImageModes(element.mode, element.image)}
                    </Box>
                  );
                }

                if (element.type === "text") {
                  return (
                    <Box key={index}>
                      {index === 3 && shopArticleSection}
                      <Container
                        sx={{
                          mb: "s10",
                          font: "body",
                          textAlign: "left",
                          color: "main"
                        }}
                      >
                        <Grid cols={[1, null, 20, null]}>
                          <Box
                            sx={{
                              gridColumn: ["span 1", null, "6 / span 10", null],
                              a: {
                                color: "main"
                              }
                            }}
                          >
                            {RichText.render(element.text)}
                          </Box>
                        </Grid>
                      </Container>
                    </Box>
                  );
                }
              })}
          </Box>
        )}
        {influencerData && (
          <Box>
            <Container>
              <Grid cols={[1, null, 20, null]}>
                <Box
                  sx={{
                    gridColumn: ["span 1", null, "6 / span 10", null]
                  }}
                >
                  <InfluencerBio influencerData={influencerData} />
                </Box>
              </Grid>
            </Container>
          </Box>
        )}
        {relatedArticles?.length > 0 && (
          <SectionWithConfigurableHeader title={translations.relatedStories}>
            <Grid2Cards>
              {relatedArticles.map((article, index) => {
                return (
                  <Box key={index}>
                    <RelatedStory
                      title={article.title}
                      description={article.description}
                      route={article.route}
                      image={article.image}
                    />
                  </Box>
                );
              })}
            </Grid2Cards>
          </SectionWithConfigurableHeader>
        )}
      </PageContainer>
    </>
  );
};

ArticlePage.getInitialPropsImmediate = props => {};
ArticlePage.getInitialProps = async props => {
  try {
    const articleUid = props.query.article;
    const { language } = props;
    const response = await prismicFetcher("article", articleUid, language);

    return {
      initialPrismicResponse: response,
      initialArticleUid: articleUid,
      lang: language
    };
  } catch (error) {
    console.error(error);
    return error;
  }
};

export default ArticlePage;
